import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Compass, Cpu, Sparkles, LineChart } from "lucide-react";
import strategicArchitectureImg from "@/assets/strategic-architecture.webp";

const features = [
  { icon: Compass, title: "Founder-side thinking", description: "We optimize for long-term founder outcomes, not billable-hour expansion." },
  { icon: Cpu, title: "Product plus architecture", description: "Every product decision considers scale, systems, operational impact, and execution realities upfront." },
  { icon: Sparkles, title: "AI with operational intent", description: "We apply AI where it creates measurable leverage, workflow clarity, and operational efficiency." },
  { icon: LineChart, title: "Investor-grade execution clarity", description: "Technology narratives, scalability thinking, and execution planning built for serious growth environments." },
];

export function WhyMitraSection() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);

  return (
    <section ref={ref} className="dark relative isolate py-24 md:py-32 overflow-hidden bg-[#0a0a0f] text-white">
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 50% at 80% 10%, hsl(33 100% 47% / 0.18), transparent 70%), radial-gradient(50% 50% at 10% 90%, hsl(248 70% 30% / 0.30), transparent 70%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
          backgroundSize: "180px 180px",
        }}
      />

      <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-6xl">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* LEFT: clean editorial portrait */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
            viewport={{ once: true, margin: "-80px" }}
            className="lg:col-span-5 relative"
          >
            <div
              aria-hidden="true"
              className="absolute -inset-6 -z-10 rounded-[2.5rem] blur-3xl opacity-70"
              style={{
                background:
                  "radial-gradient(50% 50% at 30% 30%, hsl(248 62% 21% / 0.18), transparent 70%), radial-gradient(50% 50% at 80% 80%, hsl(40 52% 58% / 0.30), transparent 70%)",
              }}
            />
            <div className="relative overflow-hidden rounded-[2rem] ring-1 ring-white/15 shadow-[0_40px_80px_-25px_rgba(0,0,0,0.7)] aspect-[4/5]">
              <motion.img
                style={{ y: imgY }}
                src={strategicArchitectureImg}
                alt="Strategic decision architecture for founder-led businesses"
                width={1024}
                height={1024}
                loading="lazy"
                className="absolute inset-0 h-[115%] w-full object-cover"
              />
              {/* glass stat overlay bottom */}
              <div className="absolute left-5 right-5 bottom-5 rounded-2xl bg-white/90 backdrop-blur-md border border-white/70 p-4 flex items-center justify-between shadow-[0_20px_40px_-15px_rgba(0,0,0,0.4)]">
                <div className="max-w-[55%]">
                  <p className="text-2xl font-display font-semibold text-[#1E1454] leading-none">50+</p>
                  <p className="mt-1 text-[11px] leading-snug text-[#1E1454]/65">Founder &amp; leadership teams advised globally</p>
                </div>
                <div className="text-right max-w-[45%]">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#F28C00] font-semibold">Across Startups &amp; Enterprise</p>
                  <p className="mt-1 text-[11px] leading-snug text-[#1E1454]/65">Product, AI &amp; platform advisory experience</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT: feature list */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true, margin: "-80px" }}
            className="lg:col-span-7"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-accent mb-4">
              Why founders work with us
            </p>
            <h2 className="font-display font-bold text-white tracking-[-0.025em] leading-[1.05] text-3xl md:text-4xl lg:text-[3rem] mb-10 max-w-xl">
              Senior strategic judgment on the decisions that move the business.
            </h2>

            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              {features.map((f, i) => (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  viewport={{ once: true }}
                  className="group rounded-2xl bg-white/[0.04] backdrop-blur-md border border-white/10 p-5 hover:border-accent/40 hover:bg-white/[0.07] transition-all duration-300 hover:-translate-y-0.5"
                >
                  <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 grid place-items-center mb-4 group-hover:bg-accent/20 group-hover:border-accent/40 transition-colors">
                    <f.icon className="h-4 w-4 text-accent" strokeWidth={1.6} />
                  </div>
                  <h3 className="text-[15px] font-display font-semibold text-white mb-1.5">{f.title}</h3>
                  <p className="text-sm text-white/65 leading-[1.65]">{f.description}</p>
                </motion.div>
              ))}
            </div>

            {/* Credibility row */}
            <div className="grid grid-cols-2 gap-4 mb-10">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-5">
                <p className="text-3xl md:text-4xl font-display font-semibold text-white leading-none">50+</p>
                <p className="mt-2 text-[12px] uppercase tracking-[0.18em] text-white/55">Founder &amp; business engagements globally</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-5">
                <p className="text-3xl md:text-4xl font-display font-semibold text-white leading-none">20+ yrs</p>
                <p className="mt-2 text-[12px] uppercase tracking-[0.18em] text-white/55">Combined strategic &amp; execution experience</p>
              </div>
            </div>

            <Link
              to="/contact"
              className="group inline-flex items-center gap-3 rounded-full bg-white text-[#1E1454] pl-3 pr-7 py-2.5 font-medium shadow-[0_22px_50px_-15px_rgba(0,0,0,0.55)] hover:shadow-[0_28px_60px_-18px_rgba(0,0,0,0.7)] transition-all duration-300 hover:-translate-y-0.5"
            >
              <span className="grid place-items-center h-9 w-9 rounded-full bg-[#1E1454] text-white transition-transform duration-300 group-hover:translate-x-0.5">
                <ArrowRight className="h-4 w-4" />
              </span>
              Schedule a Strategic Call
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
